import { cx } from '@flowus/common/cx';
import { useAppTranslation } from '@flowus/common/i18n';
import type { FC } from 'react';
import { memo } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from 'src/common/components/icon';
import { IconUiSizeBox } from 'src/components/icon-ui-size-box';
import { SelectViewBar } from '../select-view-bar';
import type { ViewModelToolBarProps } from './type';

export const ViewModelToolBar: FC<ViewModelToolBarProps> = memo((props) => {
  const { t } = useAppTranslation();
  const {
    onPdfAI,
    onChangeViewModel,
    onReplace,
    onDownLoad,
    onAddComment,
    openLink,
    splitLine = [],
    className,
    viewMode,
    hiddenOpen,
    readonly,
    onOpen,
    small,
    hiddenPreview,
  } = props;

  const iconSize = small ? 20 : 30;

  let OpenBtn = <></>;

  if (openLink && !hiddenOpen) {
    if (onOpen) {
      OpenBtn = (
        <IconUiSizeBox
          popup={t('editor.component.view_mode.fullscreen_view')}
          size={iconSize}
          className={'rounded animate-hover'}
          onClick={onOpen}
        >
          <Icon name="IcFullScreen2" size="middle" />
        </IconUiSizeBox>
      );
    } else {
      OpenBtn = (
        <NavLink to={openLink}>
          <IconUiSizeBox
            popup={t('editor.component.view_mode.fullscreen_view')}
            size={iconSize}
            className={'rounded animate-hover'}
          >
            <Icon name="IcFullScreen2" size="middle" />
          </IconUiSizeBox>
        </NavLink>
      );
    }
  }

  const DownloadBtn = (
    <IconUiSizeBox
      popup={t('common.download')}
      size={iconSize}
      onClick={onDownLoad}
      className={cx('rounded animate-hover', small && 'ml-2.5')}
    >
      <Icon name="IcDownload" size="middle" />
    </IconUiSizeBox>
  );

  if (readonly && openLink && !hiddenOpen) {
    return (
      <span className={cx('flex items-center h-full cursor-default', className)}>
        {onDownLoad && <>{DownloadBtn}</>}
        {OpenBtn}
      </span>
    );
  }

  if (readonly) return null;

  return (
    <span className={cx('flex items-center h-full cursor-default', className)}>
      {onChangeViewModel && (
        <SelectViewBar
          small={small}
          hiddenPreview={hiddenPreview}
          value={viewMode}
          onChange={onChangeViewModel}
        />
      )}
      {onPdfAI && (
        <>
          <IconUiSizeBox
            popup={t('common.pdf_assistant')}
            size={iconSize}
            onClick={onPdfAI}
            className={'rounded animate-hover'}
          >
            <Icon name="IcAi" size="middle" />
          </IconUiSizeBox>
        </>
      )}

      {onAddComment && (
        <>
          {splitLine.includes(1) && (
            <div className={cx('h-4 w-px bg-grey4 mx-4', small && 'mx-2.5')} />
          )}
          <IconUiSizeBox
            popup={t('common.add_comment')}
            size={iconSize}
            onClick={onAddComment}
            className={'rounded animate-hover'}
          >
            <Icon name="IcComment" size="middle" />
          </IconUiSizeBox>
        </>
      )}
      {splitLine.includes(1) && <div className={cx('h-4 w-px bg-grey4 mx-4', small && 'mx-2.5')} />}
      {onReplace && (
        <IconUiSizeBox
          popup={t('common.replace')}
          size={iconSize}
          onClick={onReplace}
          className={'rounded animate-hover'}
        >
          <Icon name="IcTurnInto" size="middle" />
        </IconUiSizeBox>
      )}

      {onDownLoad && DownloadBtn}
      {splitLine.includes(2) && (onReplace || onDownLoad) && !hiddenOpen && (
        <div className={cx('h-4 w-px bg-grey4 mx-4', small && 'mx-2.5')} />
      )}
      {OpenBtn}
    </span>
  );
});
